import React, { useState, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import { formatScaleValue } from '../../Signet/Studio/utils/formatter';

const useStyles = makeStyles((theme) => ({
  positionContainer: {
    minWidth: 220,
  },
  positionGrid: {
    position: 'relative',
    width: '100%',
    paddingBottom: '100%', // This makes it a perfect square
    backgroundColor: '#f5f5f5',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
    backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 0, 0, 0.1) 1px, transparent 1px)',
    backgroundSize: '20% 20%', // 5×5 grid to represent the range
    cursor: 'pointer',
    userSelect: 'none',
    overflow: 'hidden',
  },
  gridContent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  positionDot: {
    position: 'absolute',
    width: 12,
    height: 12,
    backgroundColor: theme.palette.primary.main,
    borderRadius: '50%',
    transform: 'translate(-50%, -50%)',
    cursor: 'move',
    boxShadow: '0 0 0 2px white, 0 0 4px 2px rgba(0,0,0,0.3)',
    zIndex: 4,
    transition: isDragging => isDragging ? 'none' : 'all 0.1s ease',
  },
  positionCenter: {
    position: 'absolute',
    width: 12,
    height: 12,
    border: `1px dashed ${theme.palette.grey[500]}`,
    borderRadius: '50%',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 3,
  },
  positionCoords: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    '& > div': {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
  },
  centerAxisX: {
    position: 'absolute',
    width: '100%',
    height: 2,
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
    top: '50%',
    left: 0,
    transform: 'translateY(-50%)',
    zIndex: 2,
  },
  centerAxisY: {
    position: 'absolute',
    width: 2,
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 2,
  },
  gridMarker: {
    position: 'absolute',
    fontSize: '0.6rem',
    color: theme.palette.text.secondary,
    pointerEvents: 'none',
    zIndex: 2,
  },
  gridMarkerX: {
    bottom: 2,
  },
  gridMarkerY: {
    left: 2,
  },
  scaleBadge: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.75rem',
    fontWeight: 500,
    minWidth: 60,
    textAlign: 'center',
  },
}));

const PositionGrid = ({ x, y, disabled, onPositionChange, markers }) => {
  const classes = useStyles();
  const [isDragging, setIsDragging] = useState(false);
  const gridRef = useRef(null);
  const contentRef = useRef(null);
  const dotRef = useRef(null);
  
  // Compute dynamic value range from markers
  const { minValue, range } = useMemo(() => {
    const values = markers.map(m => m.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const rng = max - min;
    return { minValue: min, range: rng };
  }, [markers]);

  // Convert dynamic range (minValue..maxValue) to grid positions (0% to 100%)
  const getGridPositionFromValues = (x, y) => {
    const xVal = x || 0;
    const yVal = y || 0;
    const gridX = (xVal - minValue) / range;
    const gridY = 1 - (yVal - minValue) / range;
    return { gridX, gridY };
  };

  // Convert grid position (0..1) to dynamic values (minValue..maxValue)
  const getValuesFromGridPosition = (gridX, gridY) => {
    const clampedX = Math.max(0, Math.min(1, gridX));
    const clampedY = Math.max(0, Math.min(1, gridY));
    const x = Math.round(clampedX * range + minValue);
    const y = Math.round((1 - clampedY) * range + minValue);
    return { x, y };
  };

  // Add a useMemo hook to cache and log gridX/gridY calculations
  const { gridX, gridY } = useMemo(() => {
    const result = getGridPositionFromValues(x, y);
    return result;
  }, [x, y]);

  const handleMouseDown = useCallback((e) => {
    if (disabled) return;
    setIsDragging(true);
    e.stopPropagation();
    e.preventDefault();
  }, [disabled]);

  const handleDocumentMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleGridClick = useCallback((e) => {
    if (disabled || !contentRef.current) return;
    
    // Don't process click if it was on the dot (let the drag handler work)
    if (e.target === dotRef.current) return;
    
    const rect = contentRef.current.getBoundingClientRect();
    const gridX = (e.clientX - rect.left) / rect.width;
    const gridY = (e.clientY - rect.top) / rect.height;
    
    const values = getValuesFromGridPosition(gridX, gridY);
    
    // Update both X and Y values
    onPositionChange(values.x, values.y);
  }, [disabled, onPositionChange]);

  const handleDocumentMouseMove = useCallback((e) => {
    if (!isDragging || !contentRef.current) return;
    
    const rect = contentRef.current.getBoundingClientRect();
    
    // Ensure we're getting the correct mouse coordinates relative to the grid
    let clientX = e.clientX;
    let clientY = e.clientY;
    
    // Handle touch events
    if (e.touches && e.touches[0]) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    }
    
    // Calculate grid position and ensure it's within bounds
    const gridX = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const gridY = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    
    const values = getValuesFromGridPosition(gridX, gridY);
    
    // Update both X and Y values
    onPositionChange(values.x, values.y);
  }, [isDragging, onPositionChange]);

  // Update event listeners to use the callback
  useLayoutEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDocumentMouseMove);
      document.addEventListener('mouseup', handleDocumentMouseUp);
      document.addEventListener('touchmove', handleDocumentMouseMove);
      document.addEventListener('touchend', handleDocumentMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleDocumentMouseMove);
        document.removeEventListener('mouseup', handleDocumentMouseUp);
        document.removeEventListener('touchmove', handleDocumentMouseMove);
        document.removeEventListener('touchend', handleDocumentMouseUp);
      };
    }
  }, [isDragging, handleDocumentMouseMove, handleDocumentMouseUp]);

  return (
    <div className={classes.positionContainer}>
      <div className={classes.positionCoords}>
        <div>
          <Typography variant="body2">X:</Typography>
          <span className={classes.scaleBadge}>
            {formatScaleValue(x || 0, disabled)}
          </span>
        </div>
        <div>
          <Typography variant="body2">Y:</Typography>
          <span className={classes.scaleBadge}>
            {formatScaleValue(y || 0, disabled)}
          </span>
        </div>
      </div>
      
      <div 
        ref={gridRef}
        className={classes.positionGrid}
        style={{ opacity: disabled ? 0.5 : 1 }}
      >
        <div 
          ref={contentRef}
          className={classes.gridContent}
          onClick={handleGridClick}
        >
          {/* Center axes */}
          <div className={classes.centerAxisX}></div>
          <div className={classes.centerAxisY}></div>
          
          {/* X-axis markers (bottom) */}
          {markers.map(marker => (
            <div 
              key={`x-${marker.value}`}
              className={`${classes.gridMarker} ${classes.gridMarkerX}`}
              style={{ left: marker.position, bottom: 0 }}
            >
              {marker.value}
            </div>
          ))}
          
          {/* Y-axis markers (left) - remember these are inverted */}
          {markers.map(marker => (
            <div 
              key={`y-${marker.value}`}
              className={`${classes.gridMarker} ${classes.gridMarkerY}`}
              style={{ top: marker.position, left: 0 }}
            >
              {-marker.value}
            </div>
          ))}
          
          <div className={classes.positionCenter}></div>
          <div 
            ref={dotRef}
            className={classes.positionDot}
            style={{ 
              left: `${gridX * 100}%`, 
              top: `${gridY * 100}%`,
              transition: isDragging ? 'none' : 'all 0.1s ease',
            }}
            onMouseDown={handleMouseDown}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default PositionGrid; 